import axios from '@/plugins/axios';
import utils from '@/stores/utils';

const state = {
    stock: null,
    order: null,
    comments: null,

    shipment: null,
    shipment_saving: false,
    shipment_saving_errors: null,
    shipment_creating: false,
    shipment_creating_errors: null,

    departure_groups: [],
    solutions_loaded: false,
    solutions_loading: null,
    solutions_loading_errors: null,

    departure_plan_complete: true,
    fulfillment_complete: false,
    vendor_fulfillment_blocks: [],
    plan_warnings: [],
    delivery_mode: null,
};

const getters = {};

function buildDepartureGroupFilterParams(group) {
    const params = {
        slice_kind: group.slice_kind,
    };
    if (group.slice_kind === 'vendor') {
        params.provider = group.provider;
    } else if (group.slice_kind === 'warehouse') {
        params.stock = group.stock;
        if (group.fulfillment_entity) {
            params.fulfillment_entity = group.fulfillment_entity;
        }
    }
    return params;
}

function buildDepartureGroupPayload(group) {
    const items = (group.shipmentitems || []).filter(
        (item) => item.enabled && item.count > 0
    );
    return {
        ...buildDepartureGroupFilterParams(group),
        items: items.map((item) => {
            const max =
                item.max != null && item.max !== ''
                    ? Number(item.max)
                    : Infinity;
            const count = Math.min(Number(item.count) || 0, max);
            return {
                product: item.product,
                count,
                ...(item.catalogentry != null
                    ? { catalogentry: item.catalogentry }
                    : {}),
            };
        }).filter((item) => item.count > 0),
    };
}

function mapGroupToShipmentItems(group) {
    group.shipmentitems = [];
    if (group.items) {
        group.shipmentitems = group.items.map((element) => {
            // Quantities (to_schedule, count) are in the order sale unit;
            // stock_count is in stock units and available_count is the
            // availability converted to the sale unit.
            const toSchedule = Number(element.to_schedule) || 0;
            let max = toSchedule;
            if (element.stock_tracking && element.available_count != null) {
                max = Math.min(
                    toSchedule,
                    Number(element.available_count) || 0
                );
            }
            return {
                count: max,
                enabled: element.allowed,
                allowed: element.allowed,
                stock_count: element.stock_count,
                available_count: element.available_count,
                unit_label: element.unit_label,
                unit_label_plural: element.unit_label_plural,
                sale_content_quantity: element.sale_content_quantity,
                sale_content_unit: element.sale_content_unit,
                catalogentry: element.catalogentry,
                line_key: element.line_key,
                stock_tracking: element.stock_tracking,
                to_schedule: toSchedule,
                max,
                product: element.product,
                product_data: element.product_data,
                sources: element.sources || [],
            };
        });
    }
    return group;
}

const mutations = {
    updateOrder(state, order) {
        state.order = order;
        state.solutions_loaded = false;
        state.departure_groups = [];
        state.comments = order.comments;
    },
    updateShipmentSaving(state, saving) {
        state.shipment_saving = saving;
    },
    updateShipmentSavingErrors(state, errors) {
        state.shipment_saving_errors = errors;
    },
    updateComments(state, comments) {
        state.comments = comments;
    },
    updateStock(state, stock) {
        state.stock = stock;
    },
    updateGroupItemCount(state, { group_key, product, count }) {
        for (const group of state.departure_groups) {
            if (group.group_key !== group_key) {
                continue;
            }
            const line = group.shipmentitems.find((item) => item.product === product);
            if (line) {
                line.count = count;
            }
        }
    },

    updateSolutions(state, payload) {
        if (payload === null) {
            state.departure_groups = [];
            state.solutions_loaded = false;
            state.solutions_loading = false;
            state.solutions_loading_errors = null;
            return;
        }

        const groups = (payload.departure_groups || []).filter(
            (group) =>
                group.slice_kind === 'warehouse' || group.slice_kind === 'vendor'
        );
        const mapped = groups.map((group) =>
            mapGroupToShipmentItems({ ...group, delivery_date: null })
        );
        state.departure_groups = mapped.sort((a, b) => {
            const a_to_schedule = a.total_to_schedule || 0;
            const b_to_schedule = b.total_to_schedule || 0;
            if (a_to_schedule === b_to_schedule) {
                return (a.group_key || '').localeCompare(b.group_key || '');
            }
            return b_to_schedule - a_to_schedule;
        });

        state.departure_plan_complete = payload.departure_plan_complete !== false;
        state.fulfillment_complete = !!payload.fulfillment_complete;
        state.vendor_fulfillment_blocks = payload.vendor_fulfillment_blocks || [];
        state.plan_warnings = payload.plan_warnings || [];
        state.delivery_mode = payload.delivery_mode || null;

        state.solutions_loaded = true;
        state.solutions_loading = false;
        state.solutions_loading_errors = null;
    },
    updateSolutionsLoading(state, solutions_loading) {
        state.solutions_loading = solutions_loading;
        state.solutions_loading_errors = null;
    },
    updateSolutionsLoadingErrors(state, solutions_loading_errors) {
        state.solutions_loading = false;
        state.solutions_loading_errors = solutions_loading_errors;
    },

    updateShipmentCreating(state, shipment_creating) {
        state.shipment_creating = shipment_creating;
        state.shipment_creating_errors = null;
    },

    updateShipmentCreatingErrors(state, shipment_creating_errors) {
        state.shipment_creating = false;
        state.shipment_creating_errors = shipment_creating_errors;
    },
    updateShipment(state, shipment) {
        state.shipment_creating = false;
        state.shipment = shipment;
    },
};

const actions = {
    async createDepartureShipment({ commit, dispatch, state }, { group, delivery_date }) {
        commit('updateShipmentCreating', true);
        const payload = {
            ...buildDepartureGroupPayload(group),
            delivery_date: delivery_date || group.delivery_date,
        };
        const masterOrderId = state.order?.id;
        try {
            const response = await axios.post(
                `/api/customerorders/${masterOrderId}/create-departure-shipment/`,
                payload
            );
            commit('updateShipment', response.data);
            dispatch('customerorder/update', null, { root: true });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateShipmentCreatingErrors', error.details);
            throw error;
        } finally {
            dispatch('session/fetchStats', null, { root: true });
        }
    },

    setOrder({ commit, dispatch }, params) {
        commit('updateOrder', params);
        commit('updateSolutions', null);
        return dispatch('fetchSolutions');
    },

    setStock({ commit, dispatch }, params) {
        commit('updateStock', params);
        return dispatch('fetchSolutions');
    },

    async fetchEligibleDepartureShipments(
        { state },
        { group, recipient, deliverysite, deliverysite_isnull }
    ) {
        const masterOrderId = state.order?.id;
        const params = {
            ...buildDepartureGroupFilterParams(group),
            recipient: recipient || undefined,
            deliverysite: deliverysite || undefined,
            deliverysite_isnull: deliverysite_isnull ? true : undefined,
        };
        Object.keys(params).forEach((key) => {
            if (params[key] === undefined) {
                delete params[key];
            }
        });
        const response = await axios.get(
            `/api/customerorders/${masterOrderId}/eligible-departure-shipments/`,
            { params }
        );
        return response;
    },

    async appendDepartureShipment({ commit, dispatch, state }, { group, shipment_id }) {
        commit('updateShipmentCreating', true);
        const masterOrderId = state.order?.id;
        const payload = {
            ...buildDepartureGroupPayload(group),
            shipment: shipment_id,
        };
        try {
            const response = await axios.post(
                `/api/customerorders/${masterOrderId}/append-departure-shipment/`,
                payload
            );
            commit('updateShipment', response.data);
            dispatch('customerorder/update', null, { root: true });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateShipmentCreatingErrors', error.details);
            throw error;
        } finally {
            commit('updateShipmentCreating', false);
            dispatch('session/fetchStats', null, { root: true });
        }
    },

    async fetchSolutions({ commit, state }, params) {
        commit('updateSolutionsLoading', true);
        const order = params?.order || state.order.id;
        const stock = params?.stock;

        const requestParams = {};
        if (stock !== undefined && stock !== null && stock !== '') {
            requestParams.stock = typeof stock === 'object' ? stock.id : stock;
        }

        try {
            const response = await axios.get(`/api/customerorders/${order}/solution/`, {
                params: requestParams,
            });
            commit('updateSolutions', response.data);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateSolutionsLoadingErrors', error.details);
            throw error;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
